import * as React from 'react';
import { clsx } from 'clsx';
import { ChevronDown } from 'lucide-react';

export interface TableFooterPaginationProps {
  /**
   * Current page number (1-indexed)
   */
  currentPage: number;
  /**
   * Total number of pages
   */
  totalPages: number;
  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;
  /**
   * Total number of results
   */
  totalResults: number;
  /**
   * Number of results currently showing
   */
  currentResults: number;
  /**
   * Items per page
   */
  itemsPerPage: number;
  /**
   * Callback when items per page changes
   */
  onItemsPerPageChange?: (itemsPerPage: number) => void;
  /**
   * Callback when expand table is clicked
   */
  onExpandTable?: () => void;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const containerStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  backgroundColor: 'white',
  paddingTop: '4px',
  paddingBottom: 0,
  paddingLeft: 0,
  paddingRight: 0,
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const textStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '1.5',
  color: '#2f2f2f',
  minWidth: '165px',
};

const paginationWrapperStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  minWidth: '165px',
  flexWrap: 'wrap',
};

const paginationButtonStyles: React.CSSProperties = {
  width: '32px',
  height: '32px',
  minWidth: '32px',
  minHeight: '32px',
  maxWidth: '32px',
  maxHeight: '32px',
  borderRadius: '99px',
  border: 'none',
  background: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  transition: 'background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  flexShrink: 0,
  WebkitTapHighlightColor: 'transparent',
  outline: 'none',
};

const paginationButtonDisabledStyles: React.CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.5,
};

const paginationLabelStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '1.5',
  color: '#2f2f2f',
};

const paginationInputWrapperStyles: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const paginationInputStyles: React.CSSProperties = {
  width: '60px',
  paddingTop: '12px',
  paddingBottom: '12px',
  paddingLeft: '16px',
  paddingRight: '16px',
  fontSize: '13px',
  fontWeight: 400,
  lineHeight: '1.5',
  color: '#2f2f2f',
  backgroundColor: 'white',
  border: '1px solid #dfdfdf',
  borderRadius: '8px',
  fontFamily: 'inherit',
  textAlign: 'center',
  outline: 'none',
};

const paginationInputFocusStyles: React.CSSProperties = {
  boxShadow: '0px 0px 0px 3px #3cad51',
};

const rightAlignedStyles: React.CSSProperties = {
  display: 'flex',
  gap: '16px',
  alignItems: 'center',
  justifyContent: 'flex-end',
  minWidth: '165px',
};

const perPageButtonStyles: React.CSSProperties = {
  height: '32px',
  backgroundColor: 'white',
  border: '1px solid #d1d1d1',
  borderRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  paddingLeft: '12px',
  paddingRight: '8px',
  paddingTop: '8px',
  paddingBottom: '8px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  outline: 'none',
};

const perPageTextStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '1.5',
  color: '#2f2f2f',
  textAlign: 'center',
};

const expandButtonStyles: React.CSSProperties = {
  height: '32px',
  minHeight: '32px',
  backgroundColor: '#efefef',
  border: 'none',
  borderRadius: '99px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: 0,
  paddingBottom: 0,
  gap: 0,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease-in-out',
  outline: 'none',
};

const expandButtonTextStyles: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: '1.5',
  color: '#2f2f2f',
  textAlign: 'center',
};

/**
 * TableFooterPagination component - Arbor Design System
 *
 * A table footer component with result count, pagination, and table controls.
 */
export const TableFooterPagination = React.forwardRef<
  HTMLDivElement,
  TableFooterPaginationProps
>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      totalResults,
      currentResults,
      itemsPerPage,
      onItemsPerPageChange,
      onExpandTable,
      className,
      style,
      'data-testid': dataTestId,
    },
    ref
  ) => {
    const [inputValue, setInputValue] = React.useState(currentPage.toString());
    const [focusedButton, setFocusedButton] = React.useState<string | null>(null);
    const [hoveredButton, setHoveredButton] = React.useState<string | null>(null);
    const [activeButton, setActiveButton] = React.useState<string | null>(null);
    const [inputFocused, setInputFocused] = React.useState(false);
    const [perPageHovered, setPerPageHovered] = React.useState(false);
    const [expandHovered, setExpandHovered] = React.useState(false);

    React.useEffect(() => {
      setInputValue(currentPage.toString());
    }, [currentPage]);

    const handleFirstPage = () => {
      if (currentPage !== 1) {
        onPageChange(1);
      }
    };

    const handlePreviousPage = () => {
      if (currentPage > 1) {
        onPageChange(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        onPageChange(currentPage + 1);
      }
    };

    const handleLastPage = () => {
      if (currentPage !== totalPages) {
        onPageChange(totalPages);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value === '' || /^\d+$/.test(value)) {
        setInputValue(value);
      }
    };

    const handleInputBlur = () => {
      setInputFocused(false);
      const pageNumber = parseInt(inputValue, 10);
      if (isNaN(pageNumber) || pageNumber < 1) {
        setInputValue(currentPage.toString());
      } else if (pageNumber > totalPages) {
        setInputValue(totalPages.toString());
        onPageChange(totalPages);
      } else if (pageNumber !== currentPage) {
        onPageChange(pageNumber);
      }
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleInputBlur();
        (e.target as HTMLInputElement).blur();
      }
    };

    const getButtonStyle = (
      buttonName: string,
      disabled: boolean
    ): React.CSSProperties => {
      if (disabled) {
        return { ...paginationButtonStyles, ...paginationButtonDisabledStyles };
      }

      const isHovered = hoveredButton === buttonName;
      const isFocused = focusedButton === buttonName;
      const isActive = activeButton === buttonName;

      const style: React.CSSProperties = {
        ...paginationButtonStyles,
      };

      if (isHovered && !isFocused && !isActive) {
        style.backgroundColor = '#efefef';
      }

      if (isActive && !isFocused) {
        style.backgroundColor = 'transparent';
      }

      if (isFocused) {
        style.boxShadow = '0px 0px 0px 3px #3cad51';
        style.backgroundColor = 'rgba(255, 255, 255, 0.01)';
      }

      return style;
    };

    const isFirstDisabled = currentPage === 1;
    const isPreviousDisabled = currentPage === 1;
    const isNextDisabled = currentPage === totalPages;
    const isLastDisabled = currentPage === totalPages;

    const resultText =
      currentResults === totalResults
        ? `Showing ${totalResults} results`
        : `Showing ${currentResults} of ${totalResults} results`;

    return (
      <div
        ref={ref}
        className={clsx('arbor-table-footer-pagination', className)}
        style={{ ...containerStyles, ...style }}
        data-testid={dataTestId}
      >
        {/* Left section - Results count */}
        <div style={textStyles}>{resultText}</div>

        {/* Middle section - Pagination */}
        <div style={paginationWrapperStyles}>
          <button
            type="button"
            onClick={handleFirstPage}
            disabled={isFirstDisabled}
            onMouseEnter={() => setHoveredButton('first')}
            onMouseLeave={() => setHoveredButton(null)}
            onMouseDown={() => setActiveButton('first')}
            onMouseUp={() => setActiveButton(null)}
            onFocus={() => setFocusedButton('first')}
            onBlur={() => setFocusedButton(null)}
            style={getButtonStyle('first', isFirstDisabled)}
            aria-label="Go to first page"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 12L3 8L7 4M11 12L7 8L11 4"
                stroke="#2f2f2f"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={handlePreviousPage}
            disabled={isPreviousDisabled}
            onMouseEnter={() => setHoveredButton('previous')}
            onMouseLeave={() => setHoveredButton(null)}
            onMouseDown={() => setActiveButton('previous')}
            onMouseUp={() => setActiveButton(null)}
            onFocus={() => setFocusedButton('previous')}
            onBlur={() => setFocusedButton(null)}
            style={getButtonStyle('previous', isPreviousDisabled)}
            aria-label="Go to previous page"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 12L6 8L10 4"
                stroke="#2f2f2f"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div style={paginationInputWrapperStyles}>
            <span style={paginationLabelStyles}>Page</span>
            <input
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              onFocus={() => setInputFocused(true)}
              onKeyDown={handleInputKeyDown}
              style={{
                ...paginationInputStyles,
                ...(inputFocused && paginationInputFocusStyles),
              }}
              aria-label={`Page ${currentPage} of ${totalPages}`}
            />
            <span style={paginationLabelStyles}>of {totalPages}</span>
          </div>

          <button
            type="button"
            onClick={handleNextPage}
            disabled={isNextDisabled}
            onMouseEnter={() => setHoveredButton('next')}
            onMouseLeave={() => setHoveredButton(null)}
            onMouseDown={() => setActiveButton('next')}
            onMouseUp={() => setActiveButton(null)}
            onFocus={() => setFocusedButton('next')}
            onBlur={() => setFocusedButton(null)}
            style={getButtonStyle('next', isNextDisabled)}
            aria-label="Go to next page"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 4L10 8L6 12"
                stroke="#2f2f2f"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={handleLastPage}
            disabled={isLastDisabled}
            onMouseEnter={() => setHoveredButton('last')}
            onMouseLeave={() => setHoveredButton(null)}
            onMouseDown={() => setActiveButton('last')}
            onMouseUp={() => setActiveButton(null)}
            onFocus={() => setFocusedButton('last')}
            onBlur={() => setFocusedButton(null)}
            style={getButtonStyle('last', isLastDisabled)}
            aria-label="Go to last page"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9 4L13 8L9 12M5 4L9 8L5 12"
                stroke="#2f2f2f"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Right section - Controls */}
        <div style={rightAlignedStyles}>
          {onItemsPerPageChange && (
            <button
              type="button"
              onClick={() => onItemsPerPageChange(itemsPerPage)}
              onMouseEnter={() => setPerPageHovered(true)}
              onMouseLeave={() => setPerPageHovered(false)}
              style={{
                ...perPageButtonStyles,
                backgroundColor: perPageHovered ? '#f5f5f5' : 'white',
              }}
              aria-label="Items per page"
            >
              <span style={perPageTextStyles}>{itemsPerPage} per page</span>
              <ChevronDown size={16} color="#2f2f2f" />
            </button>
          )}

          {onExpandTable && (
            <button
              type="button"
              onClick={onExpandTable}
              onMouseEnter={() => setExpandHovered(true)}
              onMouseLeave={() => setExpandHovered(false)}
              style={{
                ...expandButtonStyles,
                backgroundColor: expandHovered ? '#e5e5e5' : '#efefef',
              }}
              aria-label="Expand table"
            >
              <span style={expandButtonTextStyles}>Expand table</span>
            </button>
          )}
        </div>
      </div>
    );
  }
);

TableFooterPagination.displayName = 'TableFooterPagination';
